// Feature: order-returns-list
import React, { useState, useEffect } from "react";
import {
  Box,
  AppBar,
  Grid,
  Autocomplete,
  TextField,
  CircularProgress,
  Breadcrumbs,
  Link,
  Chip,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Navbar from "../../components/common/Navbar";
import ViewData from "../../components/grids/ViewDatagrid";
import SearchAppBar from "../../components/common/search";
import { Orderreturnslistdata } from "../../assets/Griddata";
import { useStyles } from "../../assets/styles/Theme";
import { currencyFormatLKR } from "../../utils/common/CurrencyFormat";
import { externalgetApi } from "../../services/external/externalApis";
import { ORDER_API_14 } from "../../api/ApiModel";
import {
  CON_ORTC_RETURN,
  CON_ORTC_REFUND,
  CON_ORTC_EXCHANGE,
  CON_STATUS_DELETED,
} from "../../utils/sys/Constants";
import VisibilityIcon from "@mui/icons-material/Visibility";

const RETURN_TYPE_LIST = [
  { code: "X",               label: "All"      },
  { code: CON_ORTC_RETURN,   label: "Return"   },
  { code: CON_ORTC_REFUND,   label: "Refund"   },
  { code: CON_ORTC_EXCHANGE, label: "Exchange" },
];

function NotesList() {
  // State
  const [returnData, setReturnData]                 = useState([]);
  const [filteredData, setFilteredData]             = useState([]);
  const [isLoading, setIsLoading]                   = useState(false);
  const [error, setError]                           = useState(null);
  const [fromDate, setFromDate]                     = useState(dayjs().startOf("month"));
  const [toDate, setToDate]                         = useState(dayjs().endOf("month"));
  const [selectedReturnType, setSelectedReturnType] = useState(RETURN_TYPE_LIST[0]);
  const [searchText, setSearchText]                 = useState("");

  // Redux
  const SYS_USER = useSelector((state) => state.user) || {};
  const { COMPCODE, LOCCODE, RESTCODE } = SYS_USER.USER_PARAM || {};

  const navigate = useNavigate();
  const theme = useTheme();
  const styles = useStyles(theme);

  // Data fetching
  const fetchReturnData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await externalgetApi(ORDER_API_14, "GET", {
        COMPCODE,
        LOCCODE,
        RESTCODE,
        FROMDATE: dayjs(fromDate).format("MM-DD-YYYY"),
        TODATE:   dayjs(toDate).format("MM-DD-YYYY"),
        STSD:     CON_STATUS_DELETED,
      });
      setReturnData(response || []);
      setFilteredData(response || []);
    } catch (err) {
      setError(err?.message || "Failed to load return data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReturnData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  // Client-side filtering: return type + text search applied together
  useEffect(() => {
    let result = returnData;

    // Filter by return type (skip when "All" / code "X")
    if (selectedReturnType.code !== "X") {
      result = result.filter(
        (record) => Number(record.RETURNTYPECODE) === Number(selectedReturnType.code)
      );
    }

    // Filter by search text
    if (searchText) {
      const lower = searchText.toLowerCase();
      result = result.filter(
        (record) =>
          (record.ODRETURNCODE || "").toLowerCase().includes(lower) ||
          (record.ORDERCODE    || "").toLowerCase().includes(lower) ||
          (record.INVNO        || "").toLowerCase().includes(lower)
      );
    }

    setFilteredData(result);
  }, [searchText, returnData, selectedReturnType]);

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  // Safely convert any value to a renderable string for plain text cells
  const safeStr = (val) => {
    if (val == null || (typeof val === "object")) return "";
    return String(val);
  };

  // Task 5.1: Map raw Return_Record objects to display rows for ViewDatagrid
  const mapResultToRows = (records) => {
    return records.map((record) => {
      const rtCode = Number(record.RETURNTYPECODE);
      let chipColor = "default";
      if (rtCode === CON_ORTC_RETURN)   chipColor = "primary";
      if (rtCode === CON_ORTC_REFUND)   chipColor = "success";
      if (rtCode === CON_ORTC_EXCHANGE) chipColor = "warning";

      return {
        ODRETURNCODE:          safeStr(record.ODRETURNCODE),
        RETURNNO:              safeStr(record.RETURNNO),
        ORDERCODE:             safeStr(record.ORDERCODE),
        INVNO:                 safeStr(record.INVNO),
        RETURNTYPEDESCRIPTION: (
          <Chip
            label={safeStr(record.RETURNTYPEDESCRIPTION) || "—"}
            color={chipColor}
            size="small"
          />
        ),
        RETURNDATE:   safeStr(record.RETURNDATE),
        REFUNDAMOUNT: (() => {
          const n = parseFloat(record.REFUNDAMOUNT);
          if (isNaN(n)) return "";
          // Format without currency label
          return new Intl.NumberFormat("en-US", {
            style: "decimal",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(n);
        })(),
        REMARK: safeStr(record.REMARK),
      };
    });
  };

  // Task 5.2: Navigate to the Return Details page for the selected record
  const handleViewClick = (ODRETURNCODE) => {
    // Compare as strings to handle API returning numbers vs strings
    const record = returnData.find(
      (item) => String(item.ODRETURNCODE) === String(ODRETURNCODE)
    );
    if (record) {
      navigate("/returndetails", {
        state: { returnRecord: record },
      });
    }
  };

  // Task 7.2: Date/type change handlers
  const handleFromDateChange = (newVal) => { if (newVal) setFromDate(newVal); };
  const handleToDateChange   = (newVal) => { if (newVal) setToDate(newVal); };
  const handleReturnTypeChange = (_, newVal) => { if (newVal) setSelectedReturnType(newVal); };

  // Task 7.2: Row context menu items
  const menuItems = [
    {
      text: "View",
      icon: <VisibilityIcon fontSize="small" />,
      onClick: (ODRETURNCODE) => handleViewClick(ODRETURNCODE),
    },
  ];

  return (
    <>
      <Navbar />
      <AppBar sx={styles.AI_CustomAppBar1}>
        <Box sx={styles.AI_HeaderContainer1}>
          <Breadcrumbs sx={styles.AI_breadcrumbs2}>
            <Link underline="hover" color="inherit" href="/">Home</Link>
            <Link underline="hover" color="inherit">Returns</Link>
            <Link underline="hover" color="text.primary">Credit/Exchange Notes</Link>
          </Breadcrumbs>
        </Box>
      </AppBar>

      <Box sx={styles.OL_AddStyledBox}>
        <Grid container spacing={2} mr={15} alignItems="center" sx={styles.OL_AddStyledBox_container}>
          {/* From Date */}
          <Grid item xs={12} md={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="From Date"
                value={fromDate}
                onChange={handleFromDateChange}
                inputFormat="MM/DD/YYYY"
                slotProps={{
                  textField: {
                    sx: {
                      width: "100%",
                      "& .MuiInputBase-root": { height: "40px" },
                      "& .MuiInputAdornment-root .MuiIconButton-root": {
                        color: theme.palette.primary.main,
                      },
                    },
                  },
                  popper: {
                    sx: {
                      "& .MuiPaper-root": {
                        backgroundColor: theme.palette.background.paper,
                        boxShadow: "none",
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          {/* To Date */}
          <Grid item xs={12} md={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="To Date"
                value={toDate}
                onChange={handleToDateChange}
                inputFormat="MM/DD/YYYY"
                slotProps={{
                  textField: {
                    sx: {
                      width: "100%",
                      "& .MuiInputBase-root": { height: "40px" },
                      "& .MuiInputAdornment-root .MuiIconButton-root": {
                        color: theme.palette.primary.main,
                      },
                    },
                  },
                  popper: {
                    sx: {
                      "& .MuiPaper-root": {
                        backgroundColor: theme.palette.background.paper,
                        boxShadow: "none",
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          {/* Return Type */}
          <Grid item xs={12} md={3}>
            <Autocomplete
              options={RETURN_TYPE_LIST}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              value={selectedReturnType}
              disableClearable
              onChange={handleReturnTypeChange}
              renderInput={(params) => (
                <TextField {...params} label="Return Type" size="small" fullWidth />
              )}
            />
          </Grid>

          {/* Search */}
          <Grid item xs={12} md={5}>
            <SearchAppBar onChange={handleSearchChange} value={searchText} />
          </Grid>
        </Grid>
      </Box>

      {/* Grid area */}
      <Box sx={styles.OL_vdg}>
        {isLoading ? (
          <CircularProgress />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <ViewData
            columns={Orderreturnslistdata.columns}
            rows={mapResultToRows(filteredData)}
            menuItems={menuItems}
            tabletitle={Orderreturnslistdata.name}
            gridid="ODRETURNCODE"
          />
        )}
      </Box>
    </>
  );
}

export default NotesList;
